import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Paperclip, AlertCircle, X, Mic } from 'lucide-react';
import { useGravadorAudio } from '@/hooks/useGravadorAudio';
import { GravadorAudioBar } from './GravadorAudioBar';
import type { Citacao } from '@/lib/citacao';
import { useEnviarMensagem } from '@/hooks/useMensagens';
import { useAuthContext } from '@/contexts/AuthContext';
import { useWhatsAppChannel } from '@/hooks/useWhatsAppChannel';
import { useWhatsAppContact } from '@/hooks/useWhatsAppContact';
import { useAutoAssumirAoResponder } from '@/hooks/useAutoAssumirAoResponder';
import { toast } from '@/hooks/use-toast';
import { validarFicheiroMedia, type TipoMediaEnvio } from '@/lib/media-validation';
import { useUploadMidia, useSendMidia } from '@/hooks/useMensagens';
import { MidiaPreview } from './MidiaPreview';
import { CitacaoBloco } from './CitacaoBloco';
import { EmojiPicker } from './EmojiPicker';
import { sendTypingIndicator } from '@/services/whatsapp.service';
import { shouldSendTyping } from '@/lib/typing-indicator';
import { insertEmoji } from '@/lib/emojis';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/hooks/useOrganization';
import { enviarMensagemEmpresaApp, uploadChatAppMedia } from '@/services/chat-app.service';

interface ChatInputProps {
  clienteId: string;
  canal?: 'app' | 'whatsapp';
  responsavelId?: string | null;
  ultimaClienteBspId?: string | null;
  disabled?: boolean;
  respostaCitacao?: Citacao | null;
  respostaBspId?: string | null;
  onCancelarResposta?: () => void;
  onTyping?: () => void;
}

export function ChatInput({ clienteId, canal = 'whatsapp', responsavelId = null, ultimaClienteBspId = null, disabled = false, respostaCitacao = null, respostaBspId = null, onCancelarResposta, onTyping }: ChatInputProps) {
  const { t } = useTranslation();
  const [mensagem, setMensagem] = useState('');
  const [isAppSending, setIsAppSending] = useState(false);
  const lastTypingRef = useRef(0);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef(0);
  const { mutate: enviarMensagem, isPending } = useEnviarMensagem();
  const { profile, organizationId } = useAuthContext();
  const autoAssumir = useAutoAssumirAoResponder();
  const gravador = useGravadorAudio();
  const { data: whatsAppContact } = useWhatsAppContact(clienteId);
  const { data: whatsAppChannel } = useWhatsAppChannel(organizationId ?? undefined);
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();

  const isAppMode = canal === 'app';
  const isWhatsAppMode =
    !isAppMode && !!whatsAppContact && !!whatsAppChannel && whatsAppChannel.status === 'connected';
  const channelError = whatsAppChannel?.status === 'error';
  const sendDisabled =
    disabled || isPending || isAppSending || !mensagem.trim() || (!isAppMode && !!whatsAppContact && channelError);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ficheiro, setFicheiro] = useState<{ file: File; tipoMedia: TipoMediaEnvio } | null>(null);
  const [storagePath, setStoragePath] = useState<string | null>(null);
  const uploadMidia = useUploadMidia();
  const sendMidia = useSendMidia();

  const limparMidia = () => {
    setFicheiro(null);
    setStoragePath(null);
  };

  const handleEscolherFicheiro = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const v = validarFicheiroMedia(f.type, f.size);
    if (!v.ok || !v.tipoMedia) {
      toast({ title: t(v.erro ?? 'panel.media.error.tipoInvalido'), variant: 'destructive' });
      return;
    }
    setFicheiro({ file: f, tipoMedia: v.tipoMedia });
    setStoragePath(null);
    // Pré-upload ao bucket já no preview → o "Enviar" fica instantâneo.
    // Em modo app o upload acontece no momento do envio, não aqui.
    if (!isAppMode) {
      uploadMidia.mutate(f, {
        onSuccess: ({ storagePath: p }) => setStoragePath(p),
      });
    }
  };

  // Helper partilhado pelos 3 branches app: auto-assumir + invalidar caches.
  const afterAppSend = () => {
    if (responsavelId == null && profile?.id) {
      autoAssumir.mutate({ clienteId, profileId: profile.id, profileNome: profile.nome || t('common.role.atendente') });
    }
    queryClient.invalidateQueries({ queryKey: ['mensagens'] });
    queryClient.invalidateQueries({ queryKey: ['clientes'] });
    queryClient.invalidateQueries({ queryKey: ['inbox-previews'] });
  };

  const handleEnviarMidia = async (caption: string) => {
    if (!ficheiro || !profile) return;

    // Branch app: upload direto para o bucket app-chat-media e grava via insert.
    if (isAppMode) {
      setIsAppSending(true);
      try {
        const path = await uploadChatAppMedia(clienteId, ficheiro.file);
        await enviarMensagemEmpresaApp({
          clienteId,
          orgId: requireOrgId(),
          conteudo: caption.trim() || '',
          autorId: profile.id,
          nomeRemetente: profile.nome || t('common.role.atendente'),
          tipoMedia: ficheiro.tipoMedia === 'audio' ? 'audio' : 'image',
          mediaStoragePath: path,
          mediaMimeType: ficheiro.file.type,
        });
        limparMidia();
        afterAppSend();
      } catch {
        toast({ title: t('panel.sendError'), description: t('panel.sendErrorDescription'), variant: 'destructive' });
      } finally {
        setIsAppSending(false);
      }
      return;
    }

    if (!storagePath) return;
    sendMidia.mutate(
      {
        clienteId,
        storagePath,
        mimeType: ficheiro.file.type,
        filename: ficheiro.file.name,
        caption: caption.trim() || undefined,
        tipoMedia: ficheiro.tipoMedia,
      },
      {
        onSuccess: () => {
          limparMidia();
          if (responsavelId == null && profile?.id) {
            autoAssumir.mutate({ clienteId, profileId: profile.id, profileNome: profile.nome || t('common.role.atendente') });
          }
        },
        onError: () => {
          toast({ title: t('panel.sendError'), description: t('panel.sendErrorDescription'), variant: 'destructive' });
        },
      },
    );
  };

  const handleEnviar = async () => {
    const texto = mensagem.trim();
    if (!texto || !clienteId || !profile) return;

    // Limpa o campo já — não espera o round-trip de envio —
    // para a próxima mensagem poder ser escrita de imediato.
    const replyTo = respostaBspId ?? undefined;
    setMensagem('');
    onCancelarResposta?.();
    messageInputRef.current?.focus();

    // Branch app: insert direto via RLS, sem WhatsApp.
    // Citação de resposta (reply-to BSP) é específica do WhatsApp — não se aplica ao canal app.
    if (isAppMode) {
      setIsAppSending(true);
      try {
        await enviarMensagemEmpresaApp({
          clienteId,
          orgId: requireOrgId(),
          conteudo: texto,
          autorId: profile.id,
          nomeRemetente: profile.nome || t('common.role.atendente'),
        });
        afterAppSend();
      } catch {
        // Devolve o texto ao campo se ainda estiver vazio.
        setMensagem((cur) => (cur ? cur : texto));
        toast({
          title: t('panel.sendError'),
          description: t('panel.sendErrorDescription'),
          variant: 'destructive',
        });
      } finally {
        setIsAppSending(false);
      }
      return;
    }

    enviarMensagem(
      {
        cliente_potencial_id: clienteId,
        remetente: 'atendente',
        conteudo: texto,
        nome_remetente: profile.nome || t('common.role.atendente'),
        canal: isWhatsAppMode ? 'whatsapp' : 'interno',
        reply_to_bsp_id: replyTo,
      },
      {
        onSuccess: () => {
          // Primeiro atendente a responder a um cliente sem responsável assume-o.
          // Fire-and-forget: o hook trata o caso de corrida perdida em silêncio.
          if (responsavelId == null && profile?.id) {
            autoAssumir.mutate({
              clienteId,
              profileId: profile.id,
              profileNome: profile.nome || t('common.role.atendente'),
            });
          }
        },
        onError: () => {
          // Devolve o texto ao campo se ainda estiver vazio (não apaga uma nova
          // mensagem entretanto começada). O utilizador pode reenviar.
          setMensagem((cur) => (cur ? cur : texto));
          toast({
            title: t('panel.sendError'),
            description: t('panel.sendErrorDescription'),
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleEnviar();
    }
  };

  // Trocar de conversa repõe o throttle (não suprime a 1ª tecla da nova).
  useEffect(() => {
    lastTypingRef.current = 0;
  }, [clienteId]);

  useEffect(() => {
    if (!gravador.erro) return;
    const msg = gravador.erro === 'permissao-negada'
      ? 'Permite o acesso ao microfone para gravar áudio.'
      : gravador.erro === 'gravacao-nao-suportada'
        ? 'O teu navegador não suporta gravação de áudio.'
        : 'Não foi possível preparar o áudio.';
    toast({ title: msg, variant: 'destructive' });
  }, [gravador.erro]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value;
    setMensagem(valor);
    onTyping?.();
    if (
      valor.trim() &&
      isWhatsAppMode &&
      ultimaClienteBspId &&
      shouldSendTyping(Date.now(), lastTypingRef.current)
    ) {
      lastTypingRef.current = Date.now();
      void sendTypingIndicator(ultimaClienteBspId);
    }
  };

  const handleEnviarAudio = async () => {
    if (!gravador.mp3Blob || !clienteId || !profile) return;
    const file = new File([gravador.mp3Blob], 'audio.mp3', { type: 'audio/mpeg' });

    // Branch app: upload para o bucket app-chat-media, sem Meta.
    if (isAppMode) {
      setIsAppSending(true);
      try {
        const path = await uploadChatAppMedia(clienteId, file);
        await enviarMensagemEmpresaApp({
          clienteId,
          orgId: requireOrgId(),
          conteudo: '',
          autorId: profile.id,
          nomeRemetente: profile.nome || t('common.role.atendente'),
          tipoMedia: 'audio',
          mediaStoragePath: path,
          mediaMimeType: 'audio/mpeg',
        });
        gravador.cancelar();
        afterAppSend();
      } catch {
        toast({ title: t('panel.sendError'), description: t('panel.sendErrorDescription'), variant: 'destructive' });
      } finally {
        setIsAppSending(false);
      }
      return;
    }

    try {
      const { storagePath: path } = await uploadMidia.mutateAsync(file);
      sendMidia.mutate(
        { clienteId, storagePath: path, mimeType: 'audio/mpeg', filename: 'audio.mp3', tipoMedia: 'audio' },
        {
          onSuccess: () => {
            gravador.cancelar();
            if (responsavelId == null && profile?.id) {
              autoAssumir.mutate({ clienteId, profileId: profile.id, profileNome: profile.nome || t('common.role.atendente') });
            }
          },
          onError: () => {
            toast({ title: t('panel.sendError'), description: t('panel.sendErrorDescription'), variant: 'destructive' });
          },
        },
      );
    } catch {
      toast({ title: t('panel.sendError'), description: t('panel.sendErrorDescription'), variant: 'destructive' });
    }
  };

  // Micro disponível no WhatsApp e no App (ambos suportam áudio).
  const mostrarMicro = (isWhatsAppMode || isAppMode) && !mensagem.trim() && !ficheiro;

  return (
    <>
      {gravador.estado !== 'idle' ? (
        <div className="painel__composer">
          <GravadorAudioBar
            estado={gravador.estado}
            tempo={gravador.tempo}
            previewUrl={gravador.previewUrl}
            enviando={sendMidia.isPending || uploadMidia.isPending || isAppSending}
            onParar={gravador.parar}
            onCancelar={gravador.cancelar}
            onEnviar={handleEnviarAudio}
          />
        </div>
      ) : (
        <>
          {ficheiro && (
            <MidiaPreview
              file={ficheiro.file}
              tipoMedia={ficheiro.tipoMedia}
              uploadPronto={isAppMode || !!storagePath}
              uploadErro={uploadMidia.isError}
              enviando={sendMidia.isPending}
              onEnviar={handleEnviarMidia}
              onCancelar={limparMidia}
            />
          )}
          {!isAppMode && channelError && whatsAppContact && (
            <div className="painel__composer-error">
              <AlertCircle size={12} />
              {t('panel.whatsAppChannelError', 'Canal WhatsApp com erro — verifique configurações')}
            </div>
          )}
          {respostaCitacao && (
            <div className="painel__reply-bar">
              <div className="painel__reply-bar-body">
                <CitacaoBloco citacao={respostaCitacao} />
              </div>
              <button type="button" aria-label={t('panel.media.cancel')} onClick={() => onCancelarResposta?.()}>
                <X size={16} />
              </button>
            </div>
          )}
          <form
            className="painel__composer"
            onSubmit={(e) => {
              e.preventDefault();
              void handleEnviar();
            }}
          >
            <label className="painel__composer-input">
              <input
                ref={messageInputRef}
                type="text"
                placeholder={t('panel.messagePlaceholder')}
                value={mensagem}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
              />
              <span className="painel__composer-attach">
                <button
                  type="button"
                  aria-label="anexar"
                  disabled={disabled || (!isWhatsAppMode && !isAppMode)}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip size={15} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleEscolherFicheiro}
                />
                <EmojiPicker
                  disabled={disabled}
                  onOpenChange={(open) => {
                    if (open) {
                      caretRef.current = messageInputRef.current?.selectionStart ?? mensagem.length;
                    } else {
                      const el = messageInputRef.current;
                      if (el) {
                        el.focus();
                        el.setSelectionRange(caretRef.current, caretRef.current);
                      }
                    }
                  }}
                  onSelect={(emoji) => {
                    const r = insertEmoji(mensagem, emoji, caretRef.current);
                    setMensagem(r.text);
                    caretRef.current = r.caret;
                  }}
                />
              </span>
            </label>
            {mostrarMicro ? (
              <button
                type="button"
                className="painel__composer-send"
                aria-label="gravar áudio"
                disabled={disabled || !gravador.suportado}
                onClick={() => { void gravador.iniciar(); }}
              >
                <Mic size={16} />
              </button>
            ) : (
              <button
                type="submit"
                className="painel__composer-send"
                disabled={sendDisabled}
                aria-label={t('panel.sendMessage', 'Enviar')}
              >
                <Send size={16} />
              </button>
            )}
          </form>
        </>
      )}
    </>
  );
}
